import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';

/**
 * Recherche prédictive branchée sur le champ de la page /search.
 *
 * Le thème embarque déjà la prédictive, mais uniquement dans la modale de
 * l'en-tête. Le champ de la page n'a qu'un formulaire nu : on tape, on valide,
 * la page recharge.
 *
 * Ce composant ENVELOPPE `search-page-input-component` sans le modifier : une
 * vingtaine de règles CSS du thème ciblent cette balise, et l'attribut `ref`
 * d'un élément est unique — le champ ne peut pas servir deux composants à la
 * fois. On récupère donc le champ par sélecteur et on pose les écouteurs à la
 * main, ce qui laisse le balisage et le style d'origine intacts.
 *
 * On ne réutilise pas non plus `predictive-search-component` : à la connexion,
 * s'il existe des produits récemment vus, il déclenche `resetSearch()` qui fait
 * `searchInput.value = ''`. Sur /search?q=queen, la requête serait effacée au
 * chargement. Ici, la valeur du champ n'est jamais touchée.
 *
 * Le rendu passe par la même section `predictive-search` que la modale : même
 * apparence, mêmes ressources (produits, collections, pages, suggestions).
 *
 * @typedef {object} Refs
 * @property {HTMLElement} suggestions - Panneau des suggestions.
 * @extends {Component<Refs>}
 */
class SearchPageSuggestComponent extends Component {
  requiredRefs = ['suggestions'];

  #controller = new AbortController();
  /** @type {AbortController | null} */
  #activeFetch = null;
  #index = -1;
  /** @type {HTMLInputElement | null} */
  #input = null;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    this.#input = this.querySelector('input[name="q"]');
    if (!this.#input) return;

    this.#input.setAttribute('autocomplete', 'off');
    this.#input.addEventListener('input', this.#onInput, { signal });
    this.#input.addEventListener('keydown', this.#onKeyDown, { signal });
    this.#input.addEventListener('focus', this.#onFocus, { signal });
    document.addEventListener('click', this.#onOutsideClick, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  #onOutsideClick = (event) => {
    if (!this.contains(/** @type {Node} */ (event.target))) this.#close();
  };

  /** Éléments sélectionnables du panneau, dans l'ordre d'affichage. */
  get #items() {
    return /** @type {HTMLElement[]} */ (
      Array.from(
        this.refs.suggestions.querySelectorAll(
          '[ref="resultsItems[]"], .predictive-search-results__card'
        )
      ).filter((el) => el instanceof HTMLElement)
    );
  }

  #highlight(index) {
    const items = this.#items;
    if (!items.length) return;

    this.#index = ((index % items.length) + items.length) % items.length;
    items.forEach((item, i) => {
      if (i === this.#index) {
        item.setAttribute('aria-selected', 'true');
        item.classList.add('keyboard-focus');
        item.scrollIntoView({ block: 'nearest' });
      } else {
        item.removeAttribute('aria-selected');
        item.classList.remove('keyboard-focus');
      }
    });
  }

  #close() {
    this.refs.suggestions.hidden = true;
    this.#index = -1;
  }

  #onInput = debounce(() => {
    const terme = this.#input?.value.trim() ?? '';
    this.#index = -1;

    // Sous deux caractères, les suggestions sont du bruit et l'appel est gâché.
    if (terme.length < 2) {
      this.#close();
      return;
    }
    this.#fetch(terme);
  }, 200);

  /** Rouvre le panneau déjà chargé quand on revient dans le champ. */
  #onFocus = () => {
    if (this.#items.length && (this.#input?.value.trim().length ?? 0) >= 2) {
      this.refs.suggestions.hidden = false;
    }
  };

  #onKeyDown = (event) => {
    if (event.key === 'Escape') {
      this.#close();
      return;
    }

    const items = this.#items;
    if (this.refs.suggestions.hidden || !items.length) return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.#highlight(this.#index + 1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.#highlight(this.#index - 1);
        break;
      case 'Enter':
        // Sans sélection, on laisse le formulaire partir vers la page de résultats.
        if (this.#index >= 0) {
          event.preventDefault();
          items[this.#index]?.querySelector('a')?.click();
        }
        break;
    }
  };

  async #fetch(terme) {
    const sectionId = this.dataset.sectionId;
    if (!sectionId) return;

    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', terme);
    url.searchParams.set('resources[limit_scope]', 'each');

    this.#activeFetch?.abort();
    const abort = new AbortController();
    this.#activeFetch = abort;

    try {
      const markup = await sectionRenderer.getSectionHTML(sectionId, false, url);
      if (abort.signal.aborted || !markup) return;

      morph(this.refs.suggestions, markup);
      this.refs.suggestions.hidden = false;
      this.refs.suggestions.querySelector('.predictive-search-results__inner')?.scrollTo(0, 0);
    } catch (error) {
      if (!abort.signal.aborted) throw error;
    }
  }
}

if (!customElements.get('search-page-suggest-component')) {
  customElements.define('search-page-suggest-component', SearchPageSuggestComponent);
}
